import { Injectable } from '@angular/core';
import { Actions, createEffect, EffectsModule, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
    distinctUntilChanged,
    filter,
    map,
    skip,
    skipUntil,
    switchMap,
    tap,
} from 'rxjs/operators';
import { AppState } from 'src/app/models/app-state';
import { RideTypeService } from 'src/app/services/ride-type.service';
import {
    LayerVisibilityAction,
    SetLayerVisibility,
} from './layer-visibility.actions';
import { selectLayerVisibility } from './layer-visibility.reducer';
import { LayerVisibilityService } from './layer-visibility.service';

@Injectable()
export class LayerVisibilityEffects {
    get$ = createEffect(() => {
        return this.action$.pipe(
            ofType(LayerVisibilityAction.getLayerVisibility), // when getLayerVisibility is called
            switchMap(() => this.rideTypeService.rideType$), // switch over to watching the ride type
            map(() => {
                // reload map layer visibility from local storage on every ride type change
                // handles switching between sno/orv on the map screen
                // handles if the specific user/ridetype settings have never been saved to local storage
                return new SetLayerVisibility(
                    this.layerVisibilityService.read() || undefined
                );
            })
        );
    });

    save$ = createEffect(
        () =>
            this.action$.pipe(
                ofType(LayerVisibilityAction.getLayerVisibility),
                switchMap(() =>
                    this.store.select(selectLayerVisibility).pipe(
                        skip(1), // skip initial store set, we don't want to save until the user has made a change
                        skipUntil(
                            this.rideTypeService.rideType$.pipe(
                                filter((rideType) => Boolean(rideType))
                            )
                        ) // don't save until a ride type has been selected
                    )
                ),
                distinctUntilChanged((prev, curr) => {
                    // don't save unless the new layer visibility state is different than the previous one
                    // check number of keys to handle new layer visibility properties being added
                    if (Object.keys(prev).length !== Object.keys(curr).length) {
                        return false;
                    }
                    for (const key of Object.keys(prev)) {
                        if (prev[key] !== curr[key]) {
                            return false;
                        }
                    }
                    return true;
                }),
                tap((state) => {
                    this.layerVisibilityService.save(state);
                })
            ),
        { dispatch: false }
    );

    constructor(
        private action$: Actions,
        private layerVisibilityService: LayerVisibilityService,
        private store: Store<AppState>,
        private rideTypeService: RideTypeService
    ) {}
}

export const layerVisibilityEffectsModule = EffectsModule.forFeature([
    LayerVisibilityEffects,
]);
